/**
 * Teacher Hiring (Phase 7 / Module 1) validation contracts.
 *
 * Mirrors the schema in `packages/db/src/schema/hiring.ts`. Used by the
 * public application form, the candidate portal, and the HR pipeline UI.
 *
 * The pipeline state machine is enforced at the API layer
 * (`apps/api/src/lib/hiring/state-machine.ts`) — these schemas only
 * validate the request payloads; transition legality is checked there.
 *
 * Spec: docs/newreq.md MODULE 1 + docs/newreq-consolidated.md §Module 1.
 * Roadmap: docs/implementation-plan-v2.md §9.7.
 */
import { z } from 'zod';
import {
  emailSchema,
  idSchema,
  nameSchema,
  paginationSchema,
  phoneSchema,
  turnstileTokenSchema,
} from './common.js';

// =========================================================================
// Pipeline state machine — typed values
// =========================================================================

/**
 * Canonical pipeline stages. Mirrors the `PipelineStage` union in the DB
 * schema; the DB column is free-form text so future stages can be added
 * without a migration. Validated here at the boundary.
 */
export const pipelineStageSchema = z.enum([
  'applied',
  'aptitude_passed',
  'aptitude_failed',
  'interview_booked',
  'interview_passed',
  'interview_failed',
  'interview_waitlisted',
  'demo_scheduled',
  'demo_in_progress',
  'selected',
  'rejected',
  'rules_passed',
  'rules_failed',
  'onboarded',
  'archived',
]);
export type PipelineStage = z.infer<typeof pipelineStageSchema>;

export const maritalStatusSchema = z.enum([
  'married',
  'single',
  'prefer_not_to_say',
]);
export type MaritalStatus = z.infer<typeof maritalStatusSchema>;

export const candidateDocumentKindSchema = z.enum([
  'certificate',
  'id_proof',
  'resume',
  'transcript',
  'other',
]);
export type CandidateDocumentKind = z.infer<typeof candidateDocumentKindSchema>;

export const interviewSlotModeSchema = z.enum(['online', 'in_person']);
export type InterviewSlotMode = z.infer<typeof interviewSlotModeSchema>;

export const interviewSlotStatusSchema = z.enum([
  'open',
  'full',
  'cancelled',
  'completed',
]);
export type InterviewSlotStatus = z.infer<typeof interviewSlotStatusSchema>;

export const interviewEvaluationDecisionSchema = z.enum([
  'pending',
  'passed',
  'failed',
  'waitlisted',
]);
export type InterviewEvaluationDecision = z.infer<
  typeof interviewEvaluationDecisionSchema
>;

export const demoDecisionSchema = z.enum(['pending', 'passed', 'failed']);
export type DemoDecision = z.infer<typeof demoDecisionSchema>;

export const probationVerdictSchema = z.enum([
  'pending',
  'on_track',
  'needs_improvement',
  'at_risk',
  'terminate',
]);
export type ProbationVerdict = z.infer<typeof probationVerdictSchema>;

// =========================================================================
// Common scalars
// =========================================================================

const isoTimestampSchema = z.string().datetime({ offset: true });
const isoDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/u, 'Date must be in YYYY-MM-DD format');

/**
 * Programme slug whitelist used in the application form's
 * `coursesPreferred` array. Free-form text in the schema so future
 * programmes can ship without a migration; validated here so an
 * attacker can't smuggle 200-char "courses" past the form.
 */
const courseSlugSchema = z
  .string()
  .trim()
  .min(2)
  .max(60)
  .regex(/^[a-z0-9-]+$/u, 'Course slugs are lowercase alphanumeric with dashes');

/**
 * Language label used in the application form's `languages` multi-select
 * (M-4). Mirrors the loose-but-bounded posture of `courseSlugSchema`: a
 * human-readable name (e.g. `Malayalam`, `Arabic`, `English`) rather than a
 * slug, since these are surfaced verbatim on the teacher profile. Bounded so
 * an attacker can't smuggle a 400-char "language" past the form.
 */
const languageSchema = z.string().trim().min(2).max(40);

/**
 * Applicant gender. Mirrors `genderSchema` in `people.ts`. `unspecified` is a
 * legitimate explicit choice ("prefer not to say"), never a silent default
 * applied downstream (M-4).
 */
export const applicantGenderSchema = z.enum(['male', 'female', 'unspecified']);
export type ApplicantGender = z.infer<typeof applicantGenderSchema>;

/**
 * Acceptable applicant age window, derived from `dateOfBirth`. The academy
 * does not hire minors and treats anything past 80 as a data-entry error.
 */
export const APPLICANT_MIN_AGE = 16;
export const APPLICANT_MAX_AGE = 80;

/**
 * Whole years between `dob` (a `YYYY-MM-DD` string) and `now`. Calendar-aware
 * (accounts for whether this year's birthday has passed) so the 16..80 gate
 * matches how a human reads an age, not a fractional-year approximation.
 * Returns `null` when the date string is unparseable.
 */
const ageFromDateOfBirth = (dob: string, now: Date = new Date()): number | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/u.exec(dob);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  // Validate the calendar date round-trips (rejects 2026-02-30 etc.).
  const parsed = new Date(Date.UTC(year, month - 1, day));
  if (
    parsed.getUTCFullYear() !== year ||
    parsed.getUTCMonth() !== month - 1 ||
    parsed.getUTCDate() !== day
  ) {
    return null;
  }
  let age = now.getUTCFullYear() - year;
  const beforeBirthdayThisYear =
    now.getUTCMonth() < month - 1 ||
    (now.getUTCMonth() === month - 1 && now.getUTCDate() < day);
  if (beforeBirthdayThisYear) age -= 1;
  return age;
};

const referralSourceSchema = z.string().trim().min(1).max(120);
const coverNoteSchema = z.string().trim().min(1).max(4000);
const qualificationSchema = z.string().trim().min(2).max(400);

// =========================================================================
// Public application intake
// =========================================================================

/**
 * POST /api/hiring/applications — the public application form payload.
 *
 * Anti-abuse: Turnstile token + per-IP rate limit (handled in the route).
 * Email is normalised at the route boundary (lower-cased + trimmed).
 */
export const teacherApplicationCreateSchema = z
  .object({
    fullName: nameSchema,
    /**
     * Real date of birth (M-4). Required at submit and bounded to 16..80 by a
     * cross-field refinement below. Supersedes the free-floating `age`
     * integer, which is retained as an optional legacy field.
     */
    dateOfBirth: isoDateSchema,
    /** Explicit gender choice (M-4); `unspecified` is an opt-in, not a default. */
    gender: applicantGenderSchema,
    /** Full postal address (M-4); supersedes the optional city-only `place`. */
    address: z.string().trim().min(5).max(500),
    /** Languages the candidate teaches in (M-4); at least one required. */
    languages: z.array(languageSchema).min(1).max(10),
    age: z.number().int().min(16).max(80).nullable().optional(),
    place: z.string().trim().min(1).max(200).nullable().optional(),
    phoneNumber: phoneSchema,
    email: emailSchema,
    qualification: qualificationSchema,
    experienceYears: z.number().int().min(0).max(60).nullable().optional(),
    maritalStatus: maritalStatusSchema.nullable().optional(),
    childrenCount: z.number().int().min(0).max(10).nullable().optional(),
    childrenAges: z
      .array(z.number().int().min(0).max(30))
      .max(10)
      .nullable()
      .optional(),
    coursesPreferred: z.array(courseSlugSchema).min(1).max(10),
    coverNote: coverNoteSchema.nullable().optional(),
    referralSource: referralSourceSchema.nullable().optional(),
    consentTerms: z.boolean(),
    consentDataProcessing: z.boolean(),
    turnstileToken: turnstileTokenSchema,
  })
  .strict()
  .refine((data) => data.consentTerms === true, {
    message: 'You must accept the terms of service',
    path: ['consentTerms'],
  })
  .refine((data) => data.consentDataProcessing === true, {
    message: 'You must consent to processing of your application data',
    path: ['consentDataProcessing'],
  })
  .refine(
    (data) => {
      const years = ageFromDateOfBirth(data.dateOfBirth);
      return years !== null && years >= APPLICANT_MIN_AGE && years <= APPLICANT_MAX_AGE;
    },
    {
      message: `Applicant must be between ${APPLICANT_MIN_AGE} and ${APPLICANT_MAX_AGE} years old`,
      path: ['dateOfBirth'],
    },
  )
  .refine(
    (data) =>
      data.childrenCount === null ||
      data.childrenCount === undefined ||
      !data.childrenAges ||
      data.childrenAges.length <= data.childrenCount,
    {
      message: "childrenAges length cannot exceed childrenCount",
      path: ['childrenAges'],
    },
  );
export type TeacherApplicationCreateInput = z.infer<
  typeof teacherApplicationCreateSchema
>;

/**
 * PATCH /api/hiring/applications/:id — HR field-level edits. All fields
 * are optional; the route applies a partial merge and audits before/after.
 */
export const teacherApplicationUpdateSchema = z
  .object({
    fullName: nameSchema.optional(),
    age: z.number().int().min(16).max(80).nullable().optional(),
    place: z.string().trim().min(1).max(200).nullable().optional(),
    phoneNumber: phoneSchema.optional(),
    email: emailSchema.optional(),
    qualification: qualificationSchema.optional(),
    experienceYears: z.number().int().min(0).max(60).nullable().optional(),
    maritalStatus: maritalStatusSchema.nullable().optional(),
    childrenCount: z.number().int().min(0).max(10).nullable().optional(),
    childrenAges: z
      .array(z.number().int().min(0).max(30))
      .max(10)
      .nullable()
      .optional(),
    coursesPreferred: z.array(courseSlugSchema).min(1).max(10).optional(),
    coverNote: coverNoteSchema.nullable().optional(),
    referralSource: referralSourceSchema.nullable().optional(),
  })
  .strict();
export type TeacherApplicationUpdateInput = z.infer<
  typeof teacherApplicationUpdateSchema
>;

export const teacherApplicationListQuerySchema = paginationSchema.extend({
  pipelineStage: z
    .union([pipelineStageSchema, z.array(pipelineStageSchema).max(20)])
    .optional(),
  q: z.string().trim().min(1).max(120).optional(),
  submittedFrom: isoTimestampSchema.optional(),
  submittedTo: isoTimestampSchema.optional(),
  hasDocuments: z
    .union([z.literal('true'), z.literal('false'), z.literal('1'), z.literal('0')])
    .optional(),
});
export type TeacherApplicationListQuery = z.infer<
  typeof teacherApplicationListQuerySchema
>;

// =========================================================================
// Document presign / finalize
// =========================================================================

/**
 * Allow-list of MIME types accepted for candidate documents. Anything
 * outside this list is rejected at presign-time so the candidate never
 * uploads a binary we won't keep.
 */
export const CANDIDATE_DOCUMENT_MIME_WHITELIST = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/webp',
] as const;

export const CANDIDATE_DOCUMENT_MAX_BYTES = 10 * 1024 * 1024; // 10 MB

export const candidateDocumentPresignSchema = z
  .object({
    kind: candidateDocumentKindSchema,
    originalFilename: z.string().trim().min(1).max(256),
    mimeType: z.string().trim().min(2).max(120),
    sizeBytes: z.number().int().positive().max(CANDIDATE_DOCUMENT_MAX_BYTES),
    /**
     * SECURITY (#7) — anonymous-upload proof. The public careers form
     * cannot authenticate, so an anonymous presign must prove it owns the
     * original applicant flow. Two optional proofs travel in the body:
     *
     *   - `uploadNonce` — a short-lived KV nonce minted by the apply route
     *     (`POST /applications`) under the apply-time Turnstile + rate
     *     limit. The route verifies it is bound to THIS `applicationId`.
     *     This is the primary, always-required-for-anonymous proof.
     *   - `turnstileToken` — an optional fresh bot-check. When supplied the
     *     route verifies it; when omitted the apply-time-minted nonce
     *     stands alone (it was already issued behind a Turnstile gate).
     *
     * Both are ignored on the authenticated path (candidate owner / HR),
     * which is gated by session identity instead.
     */
    uploadNonce: z.string().trim().min(8).max(128).optional(),
    turnstileToken: turnstileTokenSchema.optional(),
  })
  .strict()
  .refine(
    (data) =>
      (CANDIDATE_DOCUMENT_MIME_WHITELIST as readonly string[]).includes(
        data.mimeType,
      ),
    {
      message: `mimeType must be one of: ${CANDIDATE_DOCUMENT_MIME_WHITELIST.join(
        ', ',
      )}`,
      path: ['mimeType'],
    },
  );
export type CandidateDocumentPresignInput = z.infer<
  typeof candidateDocumentPresignSchema
>;

export const candidateDocumentFinalizeSchema = z
  .object({
    documentId: idSchema,
  })
  .strict();
export type CandidateDocumentFinalizeInput = z.infer<
  typeof candidateDocumentFinalizeSchema
>;

/**
 * POST /api/hiring/applications/resend-magic-link (H-6) — public,
 * Turnstile-gated + per-email rate-limited re-issue of a candidate's
 * portal magic link.
 *
 * Mirrors the privacy posture of `/api/auth/request-reset`: always returns
 * a generic 200 so the endpoint can't be used to enumerate which emails
 * have applied. The link is EMAILED — never returned in the response body.
 * A fresh link is only minted for a still-pending candidate account
 * (`primary_role='candidate' AND password_hash IS NULL AND status='pending'`),
 * the same safe-account rule enforced by `ensureCandidateUser`, so the
 * endpoint can never re-issue a session link for a credentialed / privileged
 * account (which would reintroduce the account-takeover vector).
 *
 * A dedicated schema (rather than reusing `passwordResetRequestSchema`) keeps
 * the hiring contract co-located and free to grow application-id scoping later.
 */
export const candidateResendMagicLinkSchema = z
  .object({
    email: emailSchema,
    turnstileToken: turnstileTokenSchema,
  })
  .strict();
export type CandidateResendMagicLinkInput = z.infer<
  typeof candidateResendMagicLinkSchema
>;

/**
 * Response shape for `GET /api/hiring/applications/:applicationId/documents/:documentId/download`.
 * The frontend opens `url` in a new tab; the URL is single-use and
 * expires in 5 minutes.
 */
export const candidateDocumentDownloadResponseSchema = z
  .object({
    url: z.string().url(),
    expiresAt: isoTimestampSchema,
    expiresInSeconds: z.number().int().positive(),
  })
  .strict();
export type CandidateDocumentDownloadResponse = z.infer<
  typeof candidateDocumentDownloadResponseSchema
>;

// =========================================================================
// Candidate-facing response schemas (mounted on /api/me/hiring/*)
// =========================================================================

/**
 * Item shape returned by `GET /api/me/hiring/demos`. Intentionally
 * narrower than the HR `demoClassEvaluations` row: the evaluator's display
 * name is surfaced (H-5) so the candidate knows who will assess them, but
 * the evaluator id, email, phone, sub-scores, mistakesNoted, strengthsNoted,
 * and notes remain HR-internal and are stripped. `evaluatorName` is nullable
 * because the candidate-side query `leftJoin`s `users`, so a removed
 * evaluator account surfaces as `null`.
 */
export const candidateDemoListItemSchema = z
  .object({
    id: idSchema,
    sequenceNo: z.number().int().min(1).max(3),
    scheduledAt: isoTimestampSchema,
    heldAt: isoTimestampSchema.nullable(),
    decision: demoDecisionSchema,
    topic: z.string().nullable(),
    audience: z.string().nullable(),
    evaluatorName: z.string().nullable(),
  })
  .strict();
export type CandidateDemoListItem = z.infer<typeof candidateDemoListItemSchema>;

export const candidateDemoListResponseSchema = z
  .object({
    items: z.array(candidateDemoListItemSchema),
  })
  .strict();
export type CandidateDemoListResponse = z.infer<
  typeof candidateDemoListResponseSchema
>;

/**
 * Parsed `candidate_status_events.metadata_json` blob surfaced to the
 * candidate. All fields are optional because the metadata shape varies
 * per transition kind. Documented examples:
 *
 *   - aptitude / rules decide → { runId, attemptId, scorePercent, pass,
 *                                 outcome }
 *   - HR manual override     → { manualOverride: true }
 *   - candidate book / cancel→ { bookingId, slotId }
 *   - interview evaluate     → { evaluationId, bookingId }
 *   - demo schedule          → { demoCount }
 *   - demo evaluate          → { demoSequenceNo, demoId }
 *   - all-demos auto-rule    → { failedCount, passedCount }
 *   - onboarding accept      → { teacherId, employeeId }
 *
 * The portal renders fields it recognises and ignores the rest, so we
 * deliberately keep the schema loose-but-typed instead of a discriminated
 * union per stage.
 */
export const candidateStatusEventMetadataSchema = z
  .object({
    runId: idSchema.optional(),
    attemptId: idSchema.optional(),
    scorePercent: z.number().nullable().optional(),
    pass: z.boolean().nullable().optional(),
    outcome: z.enum(['passed', 'failed']).optional(),
    manualOverride: z.boolean().optional(),
    bookingId: idSchema.optional(),
    slotId: idSchema.optional(),
    evaluationId: idSchema.optional(),
    demoCount: z.number().int().optional(),
    demoSequenceNo: z.number().int().min(1).max(3).optional(),
    demoId: idSchema.optional(),
    failedCount: z.number().int().optional(),
    passedCount: z.number().int().optional(),
    teacherId: idSchema.optional(),
    employeeId: z.string().optional(),
  })
  .passthrough();
export type CandidateStatusEventMetadata = z.infer<
  typeof candidateStatusEventMetadataSchema
>;

export const candidateStatusTimelineItemSchema = z
  .object({
    id: idSchema,
    fromStage: pipelineStageSchema.nullable(),
    toStage: pipelineStageSchema,
    reason: z.string().nullable(),
    /** Parsed structured context — null when no metadata was stored or when JSON parsing failed. */
    metadata: candidateStatusEventMetadataSchema.nullable(),
    createdAt: isoTimestampSchema,
  })
  .strict();
export type CandidateStatusTimelineItem = z.infer<
  typeof candidateStatusTimelineItemSchema
>;

// =========================================================================
// HR pipeline transitions
// =========================================================================

export const applicationTransitionSchema = z
  .object({
    toStage: pipelineStageSchema,
    reason: z.string().trim().min(1).max(2000).optional(),
  })
  .strict();
export type ApplicationTransitionInput = z.infer<
  typeof applicationTransitionSchema
>;

export const assessmentIssueSchema = z
  .object({
    /**
     * Optional explicit template id. When omitted the route picks the
     * most-recently-published template of the matching scope (so HR can
     * issue without remembering ids).
     */
    templateId: idSchema.optional(),
    /** Optional human label; falls back to the template title. */
    title: z.string().trim().min(2).max(200).optional(),
    /** Optional override of the template's pass threshold. */
    passThresholdPercent: z.number().int().min(0).max(100).optional(),
    closesAt: isoTimestampSchema.optional(),
  })
  .strict();
export type AssessmentIssueInput = z.infer<typeof assessmentIssueSchema>;

export const assessmentDecideSchema = z
  .object({
    outcome: z.enum(['passed', 'failed']),
    notes: z.string().trim().min(1).max(2000).optional(),
  })
  .strict();
export type AssessmentDecideInput = z.infer<typeof assessmentDecideSchema>;

/**
 * POST /api/hiring/applications/:id/(aptitude|rules)/override — HR
 * overrides an already-decided aptitude/rules outcome (auto- or
 * manually-decided). Distinct from `assessmentDecideSchema`: a `reason`
 * is MANDATORY because the override re-opens a settled decision and must
 * leave an explicit audit trail (`hiring.aptitude.overridden`).
 */
export const assessmentOverrideSchema = z
  .object({
    outcome: z.enum(['passed', 'failed']),
    reason: z.string().trim().min(2).max(2000),
  })
  .strict();
export type AssessmentOverrideInput = z.infer<typeof assessmentOverrideSchema>;

/**
 * POST /api/hiring/applications/:id/promote — super_admin promotes a
 * selected/rules-passed candidate to the teachers table directly,
 * bypassing the candidate-self onboarding flow on `me.ts`. Useful when
 * HR collects the rules acknowledgement out-of-band.
 *
 * `startDate` is informational (recorded in audit metadata; the
 * actual hire date is `teachers.createdAt`). `notes` is free-form audit
 * context.
 */
export const hrPromoteApplicationSchema = z
  .object({
    startDate: isoDateSchema.optional(),
    notes: z.string().trim().max(2000).optional(),
  })
  .strict();
export type HrPromoteApplicationInput = z.infer<
  typeof hrPromoteApplicationSchema
>;

// =========================================================================
// Interview slots & bookings
// =========================================================================

export const interviewSlotCreateSchema = z
  .object({
    interviewerId: idSchema,
    startsAt: isoTimestampSchema,
    endsAt: isoTimestampSchema,
    mode: interviewSlotModeSchema,
    locationOrLink: z.string().trim().min(1).max(500).nullable().optional(),
    capacity: z.number().int().min(1).max(50).default(1),
    notes: z.string().trim().min(1).max(2000).nullable().optional(),
  })
  .strict()
  .refine((data) => data.startsAt < data.endsAt, {
    message: 'startsAt must be before endsAt',
    path: ['endsAt'],
  });
export type InterviewSlotCreateInput = z.infer<typeof interviewSlotCreateSchema>;

export const interviewSlotUpdateSchema = z
  .object({
    startsAt: isoTimestampSchema.optional(),
    endsAt: isoTimestampSchema.optional(),
    mode: interviewSlotModeSchema.optional(),
    locationOrLink: z.string().trim().min(1).max(500).nullable().optional(),
    capacity: z.number().int().min(1).max(50).optional(),
    status: interviewSlotStatusSchema.optional(),
    notes: z.string().trim().min(1).max(2000).nullable().optional(),
    /**
     * Optional client-side hint of the current count of bookings in
     * `{booked, attended}`. When provided alongside `capacity`, the
     * schema refuses a capacity below the current booked count so the
     * UI can fail fast. The HR route ALSO enforces this against live
     * DB state (the schema cannot see DB state) — that route check is
     * the authoritative guard; this refinement is a defensive duplicate
     * for clients that already know the count.
     */
    currentBookedCount: z.number().int().min(0).max(50).optional(),
  })
  .strict()
  .refine(
    (data) =>
      data.startsAt === undefined ||
      data.endsAt === undefined ||
      data.startsAt < data.endsAt,
    { message: 'startsAt must be before endsAt', path: ['endsAt'] },
  )
  .refine(
    (data) =>
      data.capacity === undefined ||
      data.currentBookedCount === undefined ||
      data.capacity >= data.currentBookedCount,
    {
      message:
        'capacity cannot be reduced below the count of active bookings',
      path: ['capacity'],
    },
  );
export type InterviewSlotUpdateInput = z.infer<typeof interviewSlotUpdateSchema>;

/**
 * POST /api/hiring/interviews/slots/:slotId/book — HR books a candidate
 * into the slot on the candidate's behalf. Mirrors the candidate
 * self-book route but only carries the applicationId; the route resolves
 * the slot from the URL.
 */
export const interviewSlotBookSchema = z
  .object({
    applicationId: idSchema,
  })
  .strict();
export type InterviewSlotBookInput = z.infer<typeof interviewSlotBookSchema>;

/**
 * POST /api/hiring/bookings/:bookingId/attendance — mark a booking as
 * attended or no-show after the live interview. Only valid when the
 * booking is still in `booked`; finalised bookings (cancelled/attended/
 * no_show) are rejected by the route.
 */
export const interviewBookingAttendanceSchema = z
  .object({
    status: z.enum(['attended', 'no_show']),
  })
  .strict();
export type InterviewBookingAttendanceInput = z.infer<
  typeof interviewBookingAttendanceSchema
>;

/**
 * POST /api/hiring/bookings/:bookingId/release — release a single
 * booking back to `aptitude_passed` without cancelling the slot. Reason
 * is required so HR audit captures intent.
 */
export const interviewBookingReleaseSchema = z
  .object({
    reason: z.string().trim().min(2).max(500),
  })
  .strict();
export type InterviewBookingReleaseInput = z.infer<
  typeof interviewBookingReleaseSchema
>;

/**
 * PATCH /api/hiring/interviews/slots/:slotId/interviewer — super_admin
 * reassigns a slot to a different interviewer.
 */
export const interviewSlotInterviewerSchema = z
  .object({
    interviewerUserId: idSchema,
  })
  .strict();
export type InterviewSlotInterviewerInput = z.infer<
  typeof interviewSlotInterviewerSchema
>;

/**
 * GET /api/hiring/interviews — HR calendar list query (H-4).
 *
 * Default-active semantics: when the caller passes none of `status`,
 * `statuses`, or `includeAll=true`, the route filters to active slots
 * (`open` + `full`) so the calendar isn't cluttered with cancelled /
 * completed history. The override controls (newest-to-oldest precedence):
 *
 *   - `status=open`             → single-status filter (backwards compatible)
 *   - `statuses=cancelled,completed` → multi-status `IN (...)` filter
 *   - `includeAll=true`        → no status filter (full history)
 *
 * `status` and `statuses` are mutually exclusive (cross-field refine).
 */
export const interviewListQuerySchema = z
  .object({
    from: isoTimestampSchema.optional(),
    to: isoTimestampSchema.optional(),
    interviewerId: idSchema.optional(),
    // Single-status filter (existing — backwards compatible).
    status: interviewSlotStatusSchema.optional(),
    // Multi-status filter (new). Comma-separated in the URL.
    statuses: z
      .string()
      .trim()
      .min(1)
      .max(60)
      .transform((raw) =>
        raw
          .split(',')
          .map((s) => s.trim())
          .filter((s) => s.length > 0),
      )
      .pipe(z.array(interviewSlotStatusSchema).min(1).max(4))
      .optional(),
    // Opt out of the active-only default. Drives an HR "Show all" toggle.
    includeAll: z
      .union([z.literal('true'), z.literal('false')])
      .transform((s) => s === 'true')
      .optional(),
  })
  .strict()
  .refine((data) => !(data.status && data.statuses), {
    message: 'Pass either status or statuses, not both',
    path: ['statuses'],
  });
export type InterviewListQuery = z.infer<typeof interviewListQuerySchema>;

export const interviewBookSchema = z
  .object({
    slotId: idSchema,
    notes: z.string().trim().min(1).max(2000).optional(),
  })
  .strict();
export type InterviewBookInput = z.infer<typeof interviewBookSchema>;

export const interviewCancelSchema = z
  .object({
    reason: z.string().trim().min(1).max(2000).optional(),
  })
  .strict();
export type InterviewCancelInput = z.infer<typeof interviewCancelSchema>;

export const interviewEvaluationSchema = z
  .object({
    overallScore: z.number().int().min(0).max(100).optional(),
    communicationScore: z.number().int().min(0).max(100).optional(),
    subjectKnowledgeScore: z.number().int().min(0).max(100).optional(),
    pedagogyScore: z.number().int().min(0).max(100).optional(),
    professionalismScore: z.number().int().min(0).max(100).optional(),
    notes: z.string().trim().min(1).max(4000).optional(),
    decision: interviewEvaluationDecisionSchema,
  })
  .strict()
  .refine(
    (data) => data.decision === 'pending' || data.overallScore !== undefined,
    {
      message: 'overallScore is required when decision is not pending',
      path: ['overallScore'],
    },
  );
export type InterviewEvaluationInput = z.infer<typeof interviewEvaluationSchema>;

// =========================================================================
// Demo class evaluations
// =========================================================================

const demoScheduleEntrySchema = z
  .object({
    sequenceNo: z.number().int().min(1).max(3),
    scheduledAt: isoTimestampSchema,
    evaluatorId: idSchema,
    topic: z.string().trim().min(1).max(400).nullable().optional(),
    audience: z.string().trim().min(1).max(400).nullable().optional(),
  })
  .strict();

/**
 * Per spec §5 ("3 demo classes in 3 days"), the earliest and latest
 * scheduled demo must fall within a 72-hour window. The candidate must
 * deliver three back-to-back demos so HR can judge consistency under
 * pressure — not their best day spread over months.
 *
 * Inclusive upper bound: "the third can land exactly 72h after the first"
 * reads naturally from the spec, so `spread <= DEMO_WINDOW_MS`.
 */
export const DEMO_WINDOW_MS = 72 * 60 * 60 * 1000;

export const demoScheduleSchema = z
  .object({
    demos: z.array(demoScheduleEntrySchema).min(1).max(3),
  })
  .strict()
  .refine(
    (data) =>
      new Set(data.demos.map((d) => d.sequenceNo)).size === data.demos.length,
    { message: 'sequenceNo values must be unique within a schedule', path: ['demos'] },
  )
  .refine(
    (data) => {
      if (data.demos.length < 2) return true;
      // `scheduledAt` passed `isoTimestampSchema` in the entry schema, so
      // `Date.parse` returns a finite number — no NaN guard needed.
      const times = data.demos.map((d) => Date.parse(d.scheduledAt));
      const spread = Math.max(...times) - Math.min(...times);
      return spread <= DEMO_WINDOW_MS;
    },
    {
      message:
        'All three demos must fall within a 72-hour window (per spec: "3 demo classes in 3 days")',
      path: ['demos'],
    },
  );
export type DemoScheduleInput = z.infer<typeof demoScheduleSchema>;

export const demoEvaluationSchema = z
  .object({
    heldAt: isoTimestampSchema.optional(),
    overallScore: z.number().int().min(0).max(100).optional(),
    engagementScore: z.number().int().min(0).max(100).optional(),
    clarityScore: z.number().int().min(0).max(100).optional(),
    classroomManagementScore: z.number().int().min(0).max(100).optional(),
    mistakesNoted: z.string().trim().min(1).max(4000).nullable().optional(),
    strengthsNoted: z.string().trim().min(1).max(4000).nullable().optional(),
    notes: z.string().trim().min(1).max(4000).nullable().optional(),
    topic: z.string().trim().min(1).max(400).nullable().optional(),
    audience: z.string().trim().min(1).max(400).nullable().optional(),
    decision: demoDecisionSchema,
  })
  .strict()
  .refine(
    (data) => data.decision === 'pending' || data.overallScore !== undefined,
    {
      message: 'overallScore is required when decision is not pending',
      path: ['overallScore'],
    },
  );
export type DemoEvaluationInput = z.infer<typeof demoEvaluationSchema>;

// =========================================================================
// Probation reviews
// =========================================================================

/**
 * Base shape for a probation review payload. Kept as a plain ZodObject so
 * we can derive both the create schema (with cross-field refinement) and
 * the update schema (omits + partial — chainable methods that
 * `.refine()` strips off the ZodEffects wrapper). Do NOT export the base.
 */
const probationReviewBaseSchema = z
  .object({
    weekNo: z.number().int().min(1).max(12),
    periodStart: isoDateSchema,
    periodEnd: isoDateSchema,
    overallScore: z.number().int().min(0).max(100).optional(),
    improvementNotes: z.string().trim().min(1).max(4000).nullable().optional(),
    progressNotes: z.string().trim().min(1).max(4000).nullable().optional(),
    concerns: z.string().trim().min(1).max(4000).nullable().optional(),
    verdict: probationVerdictSchema,
  })
  .strict();

export const probationReviewCreateSchema = probationReviewBaseSchema.refine(
  (data) => data.periodStart <= data.periodEnd,
  {
    message: 'periodStart must be on or before periodEnd',
    path: ['periodEnd'],
  },
);
export type ProbationReviewCreateInput = z.infer<
  typeof probationReviewCreateSchema
>;

/**
 * PATCH /api/hiring/probation/:teacherId/reviews/:reviewId — HR edits an
 * existing weekly review. `weekNo` and the period window are immutable
 * (enforced by the `teacherWeekUnique` index — repurposing those fields
 * would conflict with a future week's row), so we omit them outright.
 */
export const probationReviewUpdateSchema = probationReviewBaseSchema
  .omit({ weekNo: true, periodStart: true, periodEnd: true })
  .partial()
  .strict();
export type ProbationReviewUpdateInput = z.infer<
  typeof probationReviewUpdateSchema
>;

/**
 * POST /api/hiring/probation/:teacherId/confirm — promote the teacher
 * out of probation after 12 successful weeks. `notes` is captured in
 * audit metadata; the column itself ships as `confirmed_at` (timestamp).
 */
export const probationConfirmSchema = z
  .object({
    notes: z.string().trim().max(2000).optional(),
  })
  .strict();
export type ProbationConfirmInput = z.infer<typeof probationConfirmSchema>;

/**
 * POST /api/hiring/probation/:teacherId/terminate — terminate a teacher
 * mid-probation. Reason is mandatory (must explain the decision in
 * audit metadata) and conforms to the same 2..2000 char window used by
 * the other HR transition reasons.
 */
export const probationTerminateSchema = z
  .object({
    reason: z.string().trim().min(2).max(2000),
  })
  .strict();
export type ProbationTerminateInput = z.infer<typeof probationTerminateSchema>;

export const probationListQuerySchema = paginationSchema.extend({
  weekNo: z.coerce.number().int().min(1).max(12).optional(),
  verdict: probationVerdictSchema.optional(),
  teacherId: idSchema.optional(),
});
export type ProbationListQuery = z.infer<typeof probationListQuerySchema>;

// =========================================================================
// State machine — valid transitions
// =========================================================================

/**
 * Authoritative transition table. Keys are the `from` stage; values are
 * the set of `to` stages reachable via that transition. Drives both the
 * runtime `validateTransition` helper (server) and is exported for the
 * tests + the portal UI's stage-progress component.
 *
 * The HR `transition` endpoint accepts any (from, to) tuple present here
 * — including the archive paths from the four terminal-ish stages. The
 * candidate-initiated transitions (book, cancel) reuse the same table.
 */
export const PIPELINE_TRANSITIONS: Readonly<
  Record<PipelineStage, ReadonlyArray<PipelineStage>>
> = Object.freeze({
  applied: ['aptitude_passed', 'aptitude_failed', 'archived', 'rejected'],
  aptitude_passed: ['interview_booked', 'archived', 'rejected'],
  aptitude_failed: ['archived'],
  interview_booked: [
    'interview_passed',
    'interview_failed',
    'interview_waitlisted',
    // Candidate cancellation rewinds the application back to bookable.
    'aptitude_passed',
    'archived',
    'rejected',
  ],
  interview_passed: ['demo_scheduled', 'archived', 'rejected'],
  interview_failed: ['archived'],
  interview_waitlisted: [
    'interview_booked',
    'demo_scheduled',
    'rejected',
    'archived',
  ],
  demo_scheduled: ['demo_in_progress', 'archived', 'rejected'],
  demo_in_progress: ['selected', 'rejected', 'archived'],
  selected: ['rules_passed', 'rules_failed', 'archived', 'rejected'],
  rejected: ['archived'],
  rules_passed: ['onboarded', 'archived'],
  rules_failed: ['selected', 'archived'],
  onboarded: ['archived'],
  archived: [],
});

// ----- Onboarding config (probation + salary tiers shown to selected candidates) --

/**
 * Editable by super_admin to customize what selected candidates see on
 * the rules / onboarding pages. Per docs/newreq.md Module 1.6.
 *
 * All fields are nullable because a fresh deployment does not have
 * agreed-upon numbers yet — the candidate UI degrades to a
 * "discussed at interview" placeholder when values are null.
 */
export const hiringOnboardingConfigSchema = z
  .object({
    probationWeeks: z.number().int().min(1).max(52).nullable(),
    probationSalaryPaise: z.number().int().min(0).max(10_000_000_00).nullable(),
    realSalaryPaise: z.number().int().min(0).max(10_000_000_00).nullable(),
    note: z.string().trim().max(2000).nullable(),
  })
  .strict();

export type HiringOnboardingConfigInput = z.infer<typeof hiringOnboardingConfigSchema>;

// ----- Interview prep config (H-2) -----------------------------------------

/**
 * Owner-editable copy shown to a candidate on the interview-booking page
 * (`/candidate/interview/book`) above the slot calendar. Per docs/newreq.md
 * Module 1 §3 the candidate must see three distinct blocks BEFORE booking:
 * what the live interview involves, the location/setup test, and how to
 * prepare. Stored in KV (mirroring `hiring:onboarding-config`) so the
 * academy owner can tune the wording without a deploy.
 *
 * All fields are nullable — a fresh deployment renders a neutral fallback
 * paragraph per block rather than blank space.
 */
export const hiringInterviewPrepConfigSchema = z
  .object({
    /** Copy describing the live interview test (format, duration, what to expect). */
    liveInterviewMarkdown: z.string().trim().max(4000).nullable(),
    /** Copy describing the location/setup test (camera, mic, browser, internet). */
    setupTestMarkdown: z.string().trim().max(4000).nullable(),
    /** Prep checklist / tips ("read X", "have Y ready"). */
    howToPrepareMarkdown: z.string().trim().max(4000).nullable(),
  })
  .strict();

export type HiringInterviewPrepConfigInput = z.infer<
  typeof hiringInterviewPrepConfigSchema
>;

// ----- Anti-cheat config (L-2) ---------------------------------------------

/**
 * Tab-switch anti-cheat thresholds for `antiCheatProfile='hiring_strict'`
 * attempts. Per docs/hiring-issues/L-2: the aptitude page already detects a
 * candidate switching tabs/windows and bumps a counter, but the server never
 * acts on it beyond a soft "flag for review" bit. These thresholds let HR
 * tune the deterrent without a deploy:
 *   - `flagAtBlurCount`  — at this many blur events, flag the attempt for HR
 *                          review (and, when `emailHrOnFlag`, email HR).
 *   - `voidAtBlurCount`  — at this many blur events, void the attempt server
 *                          side so scoring stops and the candidate is barred
 *                          from answering further.
 *
 * Stored in the `CONFIG` KV namespace (mirroring `hiring:onboarding-config`),
 * so there is no schema migration and the academy owner edits it from the
 * system UI. The void threshold must be strictly greater than the flag
 * threshold so the two stages are distinct.
 */
export const hiringAntiCheatConfigSchema = z
  .object({
    flagAtBlurCount: z.number().int().min(1).max(50),
    voidAtBlurCount: z.number().int().min(2).max(100),
    /** Send an email to HR when the flag threshold is first crossed. */
    emailHrOnFlag: z.boolean(),
  })
  .strict()
  .refine((v) => v.voidAtBlurCount > v.flagAtBlurCount, {
    message: 'voidAtBlurCount must be strictly greater than flagAtBlurCount',
    path: ['voidAtBlurCount'],
  });

export type HiringAntiCheatConfigInput = z.infer<
  typeof hiringAntiCheatConfigSchema
>;

// ----- Rules content (L-3) -------------------------------------------------

/**
 * One academy "house rule" section shown to a selected candidate before the
 * rules test (`/candidate/rules`). Per docs/newreq.md Module 1 §6. Previously
 * hardcoded in the portal; now KV-backed (`hiring:rules-content`) so the
 * academy owner can edit the wording, add/remove a rule, or reorder the list
 * without a deploy.
 *
 * `id` is a stable canonical UUID (v4 or v7 — `z.string().uuid()` accepts
 * both). The system editor mints new ids with `newId()` (UUIDv7) from
 * `@bbl/auth`; the seeded defaults use fixed UUIDv4 literals. The id lets the
 * editor reorder/remove without index churn and the candidate page key its
 * list on something stable (the old code keyed on `title`, which collides if
 * two rules share a title).
 */
export const hiringRuleSectionSchema = z
  .object({
    id: z.string().uuid(),
    title: z.string().trim().min(1).max(120),
    body: z.string().trim().min(1).max(4000),
  })
  .strict();

export type HiringRuleSection = z.infer<typeof hiringRuleSectionSchema>;

/**
 * The full editable rules payload. `sections` is capped at 20 (a sane upper
 * bound for a house-rules list) and `intro` is an optional paragraph rendered
 * above the list.
 */
export const hiringRulesContentSchema = z
  .object({
    sections: z.array(hiringRuleSectionSchema).max(20),
    intro: z.string().trim().max(2000).nullable(),
  })
  .strict();

export type HiringRulesContentInput = z.infer<typeof hiringRulesContentSchema>;

